/**
 * Zod request schemas shared by `apps/api` (validation) and `apps/web`
 * (react-hook-form resolvers), so a form and its endpoint cannot drift.
 */

import { z } from 'zod';
import { BIN_COLORS, LEVEL_CODE_RE } from '../constants';

export const resolveAnswerReviewBody = z.object({ bin: z.enum(BIN_COLORS) });
export type ResolveAnswerReviewBody = z.infer<typeof resolveAnswerReviewBody>;

/**
 * A board or grade code, by shape. Whether it EXISTS is the database's answer —
 * every board/grade column has a foreign key to its table — so a request naming
 * a board nobody created is refused where it is written, not guessed at here.
 * The one definition: `tenancy.ts` and `@cwy/core/ai` import these.
 */
export const zBoard = z.string().trim().regex(LEVEL_CODE_RE, { error: 'Unknown board.' });
export const zGrade = z.string().trim().regex(LEVEL_CODE_RE, { error: 'Unknown grade.' });

/** Required board+grade filter. Every content query is scoped by board. */
/**
 * `GET /videos/search` — what the student's video library looks for. Bounded,
 * because each miss spends YouTube quota; the same query is cached for a week.
 */
export const videoSearchQuery = z.object({
  q: z.string().trim().min(3).max(160),
});
export type VideoSearchQuery = z.infer<typeof videoSearchQuery>;

export const boardGradeQuery = z.object({
  board: zBoard,
  grade: zGrade,
});

// ── Uploads ─────────────────────────────────────────────────────────────────
//
// Content types are an allowlist, not a suggestion: the extension is derived
// from it and baked into the S3 object key.

export const UPLOAD_CONTENT_TYPES = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/heic': 'heic',
} as const;

export const PDF_CONTENT_TYPE = 'application/pdf' as const;

/** 10 MB, matching the old multer limit on teacher paper uploads. */
export const MAX_PDF_BYTES = 10 * 1024 * 1024;

/**
 * 15 MB, for an answer scan or doubt photo. Image presigns declare no size, so
 * this is enforced where the bytes are read: a full-resolution phone photo is
 * well under it, and the whole image is buffered and sent inline to the model.
 */
export const MAX_IMAGE_BYTES = 15 * 1024 * 1024;

/**
 * 100 MB, for a class book. A separate limit rather than a raised
 * `MAX_PDF_BYTES`: a whole textbook is ten times a question paper, and a paper
 * is read by a paid AI call that should never be handed one.
 */
export const MAX_BOOK_BYTES = 100 * 1024 * 1024;

/**
 * A PDF book for one classroom — `POST /tenant-admin/classrooms/:id/books/presign`.
 * The classroom comes from the path and is re-loaded with the tenant; the key
 * comes back from the server.
 */
export const classBookPresignBody = z.object({
  fileName: z.string().trim().min(1).max(255),
  contentType: z.literal(PDF_CONTENT_TYPE, { error: 'Books are PDF files.' }),
  sizeBytes: z.number().int().positive().max(MAX_BOOK_BYTES),
});

/** Registers a book after the browser has uploaded it directly to S3. */
export const classBookRegisterBody = z.object({
  title: z.string().trim().min(1, 'Give the book a title.').max(200),
  s3Key: z.string().min(1).max(400),
  fileName: z.string().trim().min(1).max(255),
  sizeBytes: z.number().int().positive().max(MAX_BOOK_BYTES),
});
export type ClassBookRegisterBody = z.infer<typeof classBookRegisterBody>;

export const scanPresignBody = z.object({
  studentPaperId: z.string().min(1),
  questionId: z.string().min(1),
  contentType: z.enum(Object.keys(UPLOAD_CONTENT_TYPES) as [keyof typeof UPLOAD_CONTENT_TYPES]),
});

export const doubtPresignBody = z.object({
  contentType: z.enum(Object.keys(UPLOAD_CONTENT_TYPES) as [keyof typeof UPLOAD_CONTENT_TYPES]),
});

/**
 * Teacher question-paper upload. Presigned like every other upload — the PDF
 * bytes go straight to S3 and the worker fetches them, so Express never proxies
 * or base64-encodes a file body.
 */
export const paperPresignBody = z.object({
  fileName: z.string().trim().min(1).max(255),
  contentType: z.literal(PDF_CONTENT_TYPE),
  sizeBytes: z.number().int().positive().max(MAX_PDF_BYTES),
});

// ── Teacher papers ─────────────────────────────────────────────────────────

/** The audience is chosen before either manual entry or PDF extraction. */
export const teacherPaperDraftBody = z.object({
  title: z.string().trim().min(1).max(200),
  classroomId: z.string().min(1),
  subjectId: z.string().min(1),
});

/** Registers a PDF after the browser has uploaded it directly to S3. */
export const teacherPaperUploadBody = teacherPaperDraftBody.extend({
  s3Key: z.string().min(1).max(300),
  fileName: z.string().trim().min(1).max(255),
});

export const zTeamPart = z.enum(['T', 'E', 'A', 'M']);

/** One step of a marking scheme — what earns the mark, and how many it carries. */
export const markingStepBody = z.object({
  step: z.string().trim().max(300),
  marks: z.number().min(0).max(100),
});

/** One editable question row in a manual or extracted paper draft. */
export const teacherPaperItemBody = z
  .object({
    index: z.number().int().min(1).max(60),
    type: z.enum(['MCQ', 'SHORT_DESC', 'MEDIUM_DESC', 'LONG_DESC']).optional(),
    question: z.string().max(4_000).default(''),
    answer: z.string().max(8_000).default(''),
    marks: z.number().positive().max(100).nullish(),
    lightHint: z.string().max(500).optional().default(''),
    strongHint: z.string().max(800).optional().default(''),
    concept: z.string().max(80).optional().default(''),
    options: z.array(z.string().trim().max(500)).max(6).nullish(),
    correctOptionIdx: z.number().int().min(0).nullish(),
    // TEAM and the teacher's settings. All optional, so a draft saved before
    // they existed still validates.
    explanation: z.string().max(4_000).optional().default(''),
    markingScheme: z.array(markingStepBody).max(20).optional().default([]),
    mistakes: z.array(z.string().max(300)).max(10).optional().default([]),
    allocatedSeconds: z.number().int().min(15).max(3_600).nullish(),
    wordLimit: z.number().int().min(5).max(1_000).nullish(),
    videoRef: z.string().trim().max(500).nullish(),
    subtopicId: z.string().max(64).nullish(),
    bankId: z.string().max(64).nullish(),
    aiDrafted: z.array(zTeamPart).max(4).optional().default([]),
  })
  .superRefine((item, ctx) => {
    const hasIdx = item.correctOptionIdx !== null && item.correctOptionIdx !== undefined;
    if (hasIdx && (!Array.isArray(item.options) || item.correctOptionIdx! >= item.options.length)) {
      ctx.addIssue({
        code: 'custom',
        message: `Q${item.index}: correct choice is out of range.`,
      });
    }
  });

export const teacherPaperEditBody = z.object({
  title: z.string().trim().min(1).max(200).optional(),
  items: z.array(teacherPaperItemBody).max(60).optional(),
});

/**
 * Draft TEAM for one row. `fill` writes only the asked parts that are blank and
 * leaves what the teacher wrote; `rewrite` replaces the asked parts.
 */
export const teacherPaperGenerateBody = z.object({
  index: z.number().int().min(1).max(60),
  parts: z.array(zTeamPart).min(1).max(4).default(['T', 'E', 'A', 'M']),
  mode: z.enum(['fill', 'rewrite']).default('fill'),
});

/** Re-tag one row of a draft against the subject's real sub-topics. */
export const teacherPaperTagBody = z.object({
  index: z.number().int().min(1).max(60),
});

/** Start a paper that already has rows — the ticked questions from "From your content". */
export const teacherPaperCreateBody = teacherPaperDraftBody.extend({
  items: z.array(teacherPaperItemBody).max(60).optional(),
});

/** Tag a question that is not on a paper yet — the Author page. */
export const conceptTagBody = z.object({
  text: z.string().trim().min(1).max(4_000),
  subjectId: z.string().min(1),
  chapterId: z.string().min(1).optional(),
});

/** Draft TEAM for a question that is not on a paper yet — the Author page. */
export const teamDraftBody = z.object({
  item: teacherPaperItemBody,
  subjectId: z.string().min(1),
  parts: z.array(zTeamPart).min(1).max(4).default(['T', 'E', 'A', 'M']),
  mode: z.enum(['fill', 'rewrite']).default('fill'),
});

/** `GET /teacher/question-bank` — one subject at a time. */
export const questionBankQuery = z.object({
  subjectId: z.string().min(1),
  q: z.string().trim().max(200).optional(),
  chapterId: z.string().max(64).optional(),
  type: z.enum(['MCQ', 'SHORT_DESC', 'MEDIUM_DESC', 'LONG_DESC']).optional(),
});

/** "From your content": pasted teaching material in, candidate questions out. */
export const contentIngestBody = z.object({
  text: z.string().trim().min(20).max(30_000),
  subjectId: z.string().min(1),
  chapterId: z.string().min(1).optional(),
  mode: z.enum(['FROM_CONTENT', 'SAME', 'SIMILAR']).default('FROM_CONTENT'),
  count: z.number().int().min(1).max(12).default(5),
});

/**
 * Author one question into the bank. The same row the paper editor edits, plus
 * where it lives. A school teacher's question goes into their own school's
 * bank; only CWY's workspace writes the shared one — the server decides which,
 * never the body.
 */
export const authorQuestionBody = z
  .object({
    item: teacherPaperItemBody,
    subtopicId: z.string().min(1),
    board: zBoard,
    grade: zGrade,
    difficulty: z.enum(['EASY', 'MEDIUM', 'HARD']).optional(),
    /** Other sub-topics it also tests — tags, not a second home. */
    tags: z.array(z.string().trim().min(1).max(60)).max(20).optional(),
  })
  .superRefine(({ item }, ctx) => {
    if (item.question.trim().length < 3) {
      ctx.addIssue({ code: 'custom', message: 'Write the question first.' });
    }
    if (item.type === 'MCQ') {
      const options = (item.options ?? []).filter((o) => o.trim());
      if (
        options.length < 2 ||
        item.correctOptionIdx === null ||
        item.correctOptionIdx === undefined
      ) {
        ctx.addIssue({ code: 'custom', message: 'An MCQ needs two options and a correct choice.' });
      }
    } else if (!item.answer.trim()) {
      ctx.addIssue({ code: 'custom', message: 'A written question needs a model answer.' });
    }
  });

// ── Profile ─────────────────────────────────────────────────────────────────

export const profileBody = z.object({
  firstName: z.string().trim().min(1).max(80).optional(),
  lastName: z.string().trim().max(80).optional(),
  // Accepted here only while unset, so onboarding can still answer the
  // questionnaire in two calls. Changing an established scope is a different
  // operation with different consequences — see academicScopeBody.
  board: zBoard.optional(),
  grade: zGrade.optional(),
  // `school` is gone. It was free text the student typed, slugified, and then
  // compared as an authorization key — so spelling a school's name correctly
  // joined it. The workspace now comes from the URL and is a foreign key.
  phone: z.string().trim().max(20).optional(),
  parentEmail: z.string().trim().email().max(255).optional(),
});

/**
 * Changing an established board/grade.
 *
 * Split out of `profileBody` deliberately. Riding along with a name or phone
 * edit is how a scope change could happen as a side effect of an unrelated save,
 * and this is not a field edit — it re-points every scoped query the student
 * makes, and closes one StudentScopePeriod to open another.
 *
 * Both fields are required even when only one is changing, so the request states
 * the intended destination rather than a delta against a row the client may have
 * read some time ago.
 */
export const academicScopeBody = z.object({
  board: zBoard,
  grade: zGrade,
});
export type AcademicScopeBody = z.infer<typeof academicScopeBody>;

// ── Exam ────────────────────────────────────────────────────────────────────

export const createStudentPaperBody = z.object({
  paperId: z.string().min(1),
});

export const attemptBody = z.object({
  questionId: z.string().min(1),
  attemptIndex: z.number().int().min(1).max(3),
  answerMethod: z.enum(['OPTION', 'SCAN', 'TYPED', 'SKIPPED']),
  /** Selected option index (OPTION) or free text (TYPED). */
  answer: z.string().max(8_000).nullable().optional(),
  /** Object key returned by the presign endpoint (SCAN). */
  s3Key: z.string().max(300).optional(),
  secondsTaken: z.number().int().min(0).max(86_400),
});

export const integrityEventBody = z.object({
  questionId: z.string().min(1).optional(),
  type: z.string().min(1).max(64),
  meta: z.record(z.string(), z.unknown()).optional(),
});

export const timeSyncBody = z.object({
  totalTimeOnTask: z.number().int().min(0).optional(),
  hintTimeSeconds: z.number().int().min(0).optional(),
  videoTimeSeconds: z.number().int().min(0).optional(),
  theoryTimeSeconds: z.number().int().min(0).optional(),
});

// ── Doubt & tutor ───────────────────────────────────────────────────────────

export const doubtBody = z
  .object({
    text: z.string().trim().max(2_000).optional(),
    s3Key: z.string().max(300).optional(),
  })
  .refine((value) => Boolean(value.text?.trim() || value.s3Key), {
    message: 'Provide either a typed question or an uploaded photo.',
  });

/**
 * The tutor is stateless, so the client resends the conversation. A `tutor` turn
 * here is therefore only a CLAIM about what the tutor said: the server screens
 * every turn, whatever its role, before any of it reaches the model.
 */
export const tutorTurnSchema = z.object({
  role: z.enum(['student', 'tutor']),
  message: z.string().max(1_000),
});

/** Reasoning shares the output cap; leave room for the tutor's visible reply. */
export const TUTOR_AI_LIMITS = {
  maxOutputTokens: 16_384,
  maxAttempts: 3,
  attemptTimeoutMs: 30_000,
} as const;

/** Cover every server attempt, with headroom for backoff and HTTP transit. */
export const TUTOR_REQUEST_TIMEOUT_MS =
  TUTOR_AI_LIMITS.maxAttempts * TUTOR_AI_LIMITS.attemptTimeoutMs + 10_000;

export const tutorSessionBody = z.object({
  questionId: z.string().min(1),
  studentHistory: z.array(tutorTurnSchema).max(8),
});

export const tutorTopicBody = z.object({
  subtopicId: z.string().min(1),
  studentHistory: z.array(tutorTurnSchema).max(8),
});

// ── Question reports ────────────────────────────────────────────────────────

export const questionReportBody = z.object({
  reason: z.string().trim().min(1).max(120),
  note: z.string().trim().max(1_000).optional(),
});

export type ScanPresignBody = z.infer<typeof scanPresignBody>;
export type DoubtPresignBody = z.infer<typeof doubtPresignBody>;
export type PaperPresignBody = z.infer<typeof paperPresignBody>;
export type TeacherPaperDraftBody = z.infer<typeof teacherPaperDraftBody>;
export type TeacherPaperUploadBody = z.infer<typeof teacherPaperUploadBody>;
export type TeacherPaperItemBody = z.infer<typeof teacherPaperItemBody>;
export type TeacherPaperEditBody = z.infer<typeof teacherPaperEditBody>;
export type TeacherPaperGenerateBody = z.infer<typeof teacherPaperGenerateBody>;
export type TeacherPaperTagBody = z.infer<typeof teacherPaperTagBody>;
export type TeacherPaperCreateBody = z.infer<typeof teacherPaperCreateBody>;
export type ConceptTagBody = z.infer<typeof conceptTagBody>;
export type TeamDraftBody = z.infer<typeof teamDraftBody>;
export type QuestionBankQuery = z.infer<typeof questionBankQuery>;
export type ContentIngestBody = z.infer<typeof contentIngestBody>;
export type AuthorQuestionBody = z.infer<typeof authorQuestionBody>;
export type ProfileBody = z.infer<typeof profileBody>;
export type AttemptBody = z.infer<typeof attemptBody>;
export type DoubtBody = z.infer<typeof doubtBody>;
export type TutorSessionBody = z.infer<typeof tutorSessionBody>;
export type TutorTopicBody = z.infer<typeof tutorTopicBody>;
